import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Content, Page } from '@backstage/core-components';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { Container, Grid, makeStyles, Typography } from '@material-ui/core';
import { TechUpdatesSections } from '@ncgt/plugin-tech-updates';
import Button from '@material-ui/core/Button';
import { useAnalytics } from '@backstage/core-plugin-api';

import AndroidSVGIcon from './Icons/AndroidIcon';
import AppleSVGIcon from './Icons/AppleIcon';
import WebSVGIcon from './Icons/WebIcon';
import NodeSVGIcon from './Icons/NodeIcon';
import PythonSVGIcon from './Icons/PythonIcon';
import OpensourcenSVGIcon from './Icons/OpensourcenSVGIcon';
import { ComplianceMessage } from '../shared';

const useStyles = makeStyles(theme => ({
  searchBar: {
    display: 'flex',
    maxWidth: '60vw',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: '6px',
    height: '60px',
    '& fieldset': { border: 'none' },
  },
  containers: {
    marginBottom: theme.spacing(4),
  },
  dividier: {
    marginTop: theme.spacing(4),
  },

  root: {
    marginTop: theme.spacing(10),
    height: '100vh',
    '& input + div': {
      display: 'none',
    },
    '& input': {
      paddingLeft: '10px',
      paddingRight: '30px',
    },
    '& [data-testid="search-bar-next"]>:first-child': {
      position: 'absolute',
      right: 0,
    },
    '& [data-testid="search-bar-next"] button': {
      color: 'rgba(26,115,232,1)',
    },
  },
}));

export const HomePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const analytics = useAnalytics();

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Content>
          <Container maxWidth="md" className={classes.root}>
            <Grid container alignItems="center" justifyContent="center">
              <Grid container classes={{ root: classes.containers }}>
                <Grid item xs={12}>
                  <Typography variant="h2" component="h1" color="primary">
                    Welcome to the Global Backstage Catalog
                  </Typography>
                  <Typography variant="body1">
                    Browse News Corp’s brands and business units code libraries,
                    documentation, and more.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <ComplianceMessage />
                </Grid>
                <Grid item xs={12}>
                  <HomePageSearchBar
                    classes={{ root: classes.searchBar }}
                    placeholder="What are you looking for?"
                    debounceTime={10}
                  />
                </Grid>
              </Grid>
              <Grid container classes={{ root: classes.containers }}>
                <Grid item xs={12}>
                  <Typography variant="h3" component="h2" color="primary">
                    Search by platform
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="default"
                        startIcon={<AppleSVGIcon />}
                        onClick={() => {
                          analytics.captureEvent('click', 'home', {
                            attributes: {
                              event_category: 'click',
                              event_label: 'home',
                              value: 'catalog_ios',
                            },
                          });
                          navigate('/catalog?filters[tags]=ios', {
                            replace: true,
                          });
                        }}
                      >
                        iOS
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="default"
                        startIcon={<AndroidSVGIcon />}
                        onClick={() => {
                          analytics.captureEvent('click', 'home', {
                            attributes: {
                              event_category: 'click',
                              event_label: 'home',
                              value: 'catalog_android',
                            },
                          });
                          navigate('/catalog?filters[tags]=android', {
                            replace: true,
                          });
                        }}
                      >
                        Android
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="default"
                        startIcon={<WebSVGIcon />}
                        onClick={() => {
                          analytics.captureEvent('click', 'home', {
                            attributes: {
                              event_category: 'click',
                              event_label: 'home',
                              value: 'catalog_web',
                            },
                          });
                          navigate('/catalog?filters[tags]=web', {
                            replace: true,
                          });
                        }}
                      >
                        Web
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="default"
                        startIcon={<NodeSVGIcon />}
                        onClick={() => {
                          analytics.captureEvent('click', 'home', {
                            attributes: {
                              event_category: 'click',
                              event_label: 'home',
                              value: 'catalog_nodejs',
                            },
                          });
                          navigate('/catalog?filters[tags]=nodejs', {
                            replace: true,
                          });
                        }}
                      >
                        NodeJS
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="default"
                        startIcon={<PythonSVGIcon />}
                        onClick={() => {
                          analytics.captureEvent('click', 'home', {
                            attributes: {
                              event_category: 'click',
                              event_label: 'home',
                              value: 'catalog_python',
                            },
                          });
                          navigate('/catalog?filters[tags]=python', {
                            replace: true,
                          });
                        }}
                      >
                        Python
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="default"
                        startIcon={<OpensourcenSVGIcon />}
                        onClick={() => {
                          analytics.captureEvent('click', 'home', {
                            attributes: {
                              event_category: 'click',
                              event_label: 'home',
                              value: 'catalog_open-source',
                            },
                          });
                          navigate('/catalog?filters[tags]=open-source', {
                            replace: true,
                          });
                        }}
                      >
                        Open source
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container classes={{ root: classes.containers }}>
                <Grid item xs={12}>
                  <Typography variant="h3" component="h2" color="primary">
                    Contribute to our Catalog
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      analytics.captureEvent('click', 'home', {
                        attributes: {
                          event_category: 'click',
                          event_label: 'home',
                          value: 'contribute component',
                        },
                      });
                      navigate('/create');
                    }}
                  >
                    CONTRIBUTE COMPONENT
                  </Button>
                </Grid>
              </Grid>
              <Grid container classes={{ root: classes.containers }}>
                <Grid item xs={12}>
                  <Typography variant="h3" component="h2" color="primary">
                    Tech Updates
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TechUpdatesSections numOfSections={3} />
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
